import type { ComponentDefinition, FormComponent, FormDocument, FormParameters, FormTemplate } from '@formbird/types';
import { createSignal, createEffect, onCleanup, Show } from 'solid-js';
import { Subscription } from 'rxjs';
import { ValidationDisplay, OpenInBrowserIcon, sclUtilUrlService } from '@formbird/solid-shared';

interface UrlComponentDefinition extends ComponentDefinition {
  caption?: string;
  onClick?: string;
  style?: string;
  openIn?: string;
}

interface UrlProps {
  document: FormDocument;
  template: FormTemplate;
  componentDefinition: UrlComponentDefinition;
  fieldValue: string;
  fieldName: string;
  previousFieldValue: string;
  key: number;
  formParameters: FormParameters;
}

export function Url(props: UrlProps) {
  const subs = new Subscription();

  // Create signals for reactive state
  const [input, setInput] = createSignal(props.fieldValue ?? '');
  const [componentDef, setComponentDef] = createSignal<UrlComponentDefinition>(props.componentDefinition);

  // Get services from global injector
  const changedDocumentService = (globalThis as any).FormbirdServiceInjector.get('ChangedDocumentService');

  // Subscribe to value changes from the service
  const docSub = changedDocumentService
    .watch(props.fieldName, props.document?.documentId, null)
    .subscribe((newValue: any) => {
      setInput(newValue ?? '');
    });
  subs.add(docSub);

  // Subscribe to component definition changes
  const compSub = changedDocumentService
    .watchComponentDef(props.fieldName, props.template?.documentId)
    .subscribe((compDef: any) => {
      const updated = {
        ...componentDef(),
        ...compDef
      };
      setComponentDef(updated);
    });
  subs.add(compSub);

  // Cleanup subscriptions when component is destroyed
  onCleanup(() => {
    subs.unsubscribe();
  });

  // Derived values
  const isDisabled = () => componentDef()?.enabled === false;
  const widthClass = () => componentDef()?.fullWidth === false ? 'w-72' : 'w-full';

  // Effect to handle value changes
  createEffect(() => {
    if (isDisabled()) return;

    const changeInfo: FormComponent = {
      document: props.document,
      template: props.template,
      componentDefinition: componentDef(),
      fieldValue: input(),
      fieldName: props.fieldName,
      formParameters: props.formParameters,
      key: props.key,
    };

    changedDocumentService.valueChanged(changeInfo, input());
  });

  const handleInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    setInput(target.value);
  };

  const triggerOpenLink = () => {
    const url = input();
    if (!url) return;
    const openMode = componentDef()?.openIn || 'currentWindow';
    sclUtilUrlService.openLink(url, openMode, undefined);
  };

  const showOpenButton = () => {
    const value = input();
    return value && value.trim();
  };

  return (
    <div class={`sb-url-container ${widthClass()}`}>
      <div class="sb-url-field">
        <div class="sb-url-input-wrap">
          <input
            type="url"
            id={`solid-url-${props.document?.documentId}`}
            placeholder=" "
            class={`sb-url-input ${isDisabled() ? 'cursor-not-allowed' : ''}`}
            value={input()}
            onInput={handleInput}
            disabled={isDisabled()}
          />
          <label
            for={`solid-url-${props.document?.documentId}`}
            class={`sb-url-label${componentDef()?.mandatory ? ' ft-mandatory-label' : ''}`}
          >
            {componentDef()?.label}
            {componentDef()?.mandatory && ' *'}
          </label>
        </div>

        <Show when={showOpenButton()}>
          <button
            type="button"
            onClick={triggerOpenLink}
            class="sb-url-open-btn"
            aria-label="open_in_browser"
          >
            <OpenInBrowserIcon />
          </button>
        </Show>
      </div>

      {props.document && props.componentDefinition && (
        <ValidationDisplay document={props.document} componentDefinition={componentDef()} />
      )}
    </div>
  );
}
